import { Controller } from "@hotwired/stimulus";

/**
 * Auto-configures question type and shows a read-only options preview
 * when a publishable key is selected.
 *
 * Metadata is passed as a JSON value attribute mapping each publishable
 * key to its expected questionType and options array.
 */
export default class extends Controller {
  static values = {
    metadata: { type: Object, default: {} },
  };

  static targets = [
    "questionType",
    "titleInput",
    "optionsWrapper",
    "optionsContainer",
    "preview",
  ];

  declare readonly metadataValue: Record<
    string,
    { questionType: string; title: string; options: { label: string }[] }
  >;
  declare readonly questionTypeTarget: HTMLSelectElement;
  declare readonly titleInputTarget: HTMLInputElement;
  declare readonly hasTitleInputTarget: boolean;
  declare readonly optionsWrapperTarget: HTMLElement;
  declare readonly optionsContainerTarget: HTMLElement;
  declare readonly previewTarget: HTMLElement;
  declare readonly hasPreviewTarget: boolean;

  change(event: Event) {
    const select = event.target as HTMLSelectElement;
    const key = select.value;

    if (!key) {
      this.hidePreview();
      return;
    }

    const meta = this.metadataValue[key];
    if (!meta) {
      this.hidePreview();
      return;
    }

    // Prefill title if empty
    if (this.hasTitleInputTarget && !this.titleInputTarget.value.trim()) {
      this.titleInputTarget.value = meta.title;
    }

    // Update question type and trigger the question-type controller's toggle
    this.questionTypeTarget.value = meta.questionType;
    this.questionTypeTarget.dispatchEvent(new Event("change", { bubbles: true }));

    if (meta.options.length > 0) {
      this.showPreview(meta.options);
      this.hideEditableOptions();
    } else {
      this.hidePreview();
    }
  }

  private showPreview(options: { label: string }[]) {
    if (!this.hasPreviewTarget) return;

    const list = options
      .map((opt) => `<li class="text-sm py-1">${this.escapeHtml(opt.label)}</li>`)
      .join("");

    this.previewTarget.innerHTML = `
      <div class="mt-3">
        <div class="text-xs text-slate-500 mb-1">Options will be set automatically on save:</div>
        <ul class="list-disc list-inside bg-base-100 rounded-lg p-3 border border-base-300">
          ${list}
        </ul>
      </div>
    `;
    this.previewTarget.style.display = "block";
  }

  private hidePreview() {
    if (!this.hasPreviewTarget) return;
    this.previewTarget.style.display = "none";
    this.previewTarget.innerHTML = "";
  }

  private hideEditableOptions() {
    // Mark existing options for destruction so they don't interfere
    const destroyFields = this.optionsContainerTarget.querySelectorAll(
      'input[name*="_destroy"]',
    );
    destroyFields.forEach((field) => {
      (field as HTMLInputElement).value = "1";
    });

    // Hide the nested fields visually
    const nestedFields =
      this.optionsContainerTarget.querySelectorAll(".nested-fields");
    nestedFields.forEach((field) => {
      (field as HTMLElement).style.display = "none";
    });
  }

  private escapeHtml(text: string): string {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}
